import { MsalProvider } from "@azure/msal-react";
import { ClickAnalyticsPlugin } from "@microsoft/applicationinsights-clickanalytics-js";
import {
    AppInsightsErrorBoundary,
    ReactPlugin,
} from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "azure-maps-control/dist/atlas.min.css";
import { Toaster } from "react-hot-toast";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Header from "src/components/header";
import { queryClient } from "src/lib/react-query";
import Account from "./apps/customer-account-team-management/pages/account";
import { CustomerAccountTeamManagementRoutes } from "./apps/customer-account-team-management/routes";
import Geo from "./apps/customer-geo-locations/pages/geo";
import { CustomerGeoLocationsRoutes } from "./apps/customer-geo-locations/routes";
import ServiceFailures from "./apps/customer-service-failures/pages/serviceFailures";
import { CustomerServiceFailuresRoutes } from "./apps/customer-service-failures/routes";
import { OrderInvoicingRoutes } from "./apps/order-invoicing/routes";
import { TabletInventoryManagementRoutes } from "./apps/tablet-inventory-management/routes";
import { TrackingPartnerManagementRoutes } from "./apps/tracking-partner-management/routes";
import { RolesContextProvider } from "./context/roles-context";
import "./index.css";
import { AuthLayout } from "./layouts/auth";
import { msalInstance } from "src/lib/msal";
import IndexPage from "./pages";
import { RateCalculatorRoutes } from "./apps/rate-calculator/routes";
import { DriverRoadTestRoutes } from "./apps/driver-road-test/routes";
import { VersionInfo } from "./components/version-info";
import { HRConfigManagement } from "./apps/hr-config-management/routes";
import { ITAdminRoute } from "./apps/it-admin/routes";

const reactPlugin = new ReactPlugin();
const clickPluginInstance = new ClickAnalyticsPlugin();
const clickPluginConfig = {
    autoCapture: true,
};

if (import.meta.env.MODE !== "development") {
    const appInsights = new ApplicationInsights({
        config: {
            connectionString: import.meta.env
                .VITE_APPLICATION_INSIGHTS_CONNECTION_STRING,
            enableAutoRouteTracking: true,
            extensions: [reactPlugin, clickPluginInstance],
            extensionConfig: {
                [clickPluginInstance.identifier]: clickPluginConfig,
            },
        },
    });

    appInsights.loadAppInsights();
}

export function App() {
    return (
        <AppInsightsErrorBoundary
            onError={() => (
                <h1>
                    An error occured and application was un able to recover.
                </h1>
            )}
            appInsights={reactPlugin}
        >
            <>
                <VersionInfo />
                {import.meta.env.VITE_DEPLOYMENT === "DEV" ? (
                    <div className="text-center text-error-content bg-error font-semibold">
                        Development Mode
                    </div>
                ) : null}
                <Toaster position="top-center" />
                <MsalProvider instance={msalInstance}>
                    <AuthLayout>
                        <RolesContextProvider>
                            <QueryClientProvider client={queryClient}>
                                <ReactQueryDevtools initialIsOpen={false} />
                                <BrowserRouter>
                                    <div className="bg-gray-800 flex flex-col h-full">
                                        <Header />
                                        <main className="flex-1">
                                            <Routes>
                                                <Route
                                                    path="/"
                                                    element={<IndexPage />}
                                                />
                                                <Route
                                                    path="driver-road-test"
                                                    element={
                                                        <DriverRoadTestRoutes />
                                                    }
                                                />
                                                <Route
                                                    path="/customer-geo-locations/*"
                                                    element={
                                                        <CustomerGeoLocationsRoutes />
                                                    }
                                                />
                                                <Route
                                                    path="/customer-account-team-management/*"
                                                    element={
                                                        <CustomerAccountTeamManagementRoutes />
                                                    }
                                                />
                                                <Route
                                                    path="/customer-service-failures/*"
                                                    element={
                                                        <CustomerServiceFailuresRoutes />
                                                    }
                                                />
                                                <Route
                                                    path="/tablet-inventory-management/*"
                                                    element={
                                                        <TabletInventoryManagementRoutes />
                                                    }
                                                />
                                                <Route
                                                    path="/order-invoicing/*"
                                                    element={
                                                        <OrderInvoicingRoutes />
                                                    }
                                                />
                                                <Route
                                                    path="/geo"
                                                    element={<Geo />}
                                                />
                                                <Route
                                                    path="/account"
                                                    element={<Account />}
                                                />
                                                <Route
                                                    path="/serviceFailures"
                                                    element={
                                                        <ServiceFailures />
                                                    }
                                                />
                                                <Route
                                                    path="/serviceFailure/:orderNumber"
                                                    element={
                                                        <ServiceFailures />
                                                    }
                                                />
                                                <Route
                                                    path="/tracking-partner-management/*"
                                                    element={
                                                        <TrackingPartnerManagementRoutes />
                                                    }
                                                />
                                                <Route
                                                    path="/rate-calculator/*"
                                                    element={
                                                        <RateCalculatorRoutes />
                                                    }
                                                />
                                                <Route 
                                                    path = "/hr-config-management/*"
                                                    element = {
                                                        <HRConfigManagement />
                                                    }
                                                />
                                                <Route 
                                                    path = "/it-admin/*"
                                                    element = {
                                                        <ITAdminRoute />
                                                    }
                                                />
                                                <Route
                                                    path="*"
                                                    element={
                                                        <div>Not Found</div>
                                                    }
                                                />
                                            </Routes>
                                        </main>
                                    </div>
                                </BrowserRouter>
                            </QueryClientProvider>
                        </RolesContextProvider>
                    </AuthLayout>
                </MsalProvider>
            </>
        </AppInsightsErrorBoundary>
    );
}
